import type { GridProps } from '@mui/material';
import axios from 'axios';

import { CommonError } from '@/utils/errorUtils';

import type { TAddress } from '@/types/common';
import type {
  TCreateStationForm,
  TCreateStationPayload,
  TCreateStationResult,
  TStationOperationHours,
} from '@/types/station';

type TTimeZoneResponse = {
  dstOffset: number;
  rawOffset: number;
  timeZoneId: string;
  timeZoneName: string;
};

const getTimezone = async ({ lat, lon }: TAddress) => {
  const timestamp = Math.floor(Date.now() / 1000);
  const apiUrl = 'https://maps.googleapis.com/maps/api/timezone/json';
  const params = `location=${lat}%2c${lon}&key=${window.env.GOOGLE_API_KEY}&timestamp=${timestamp}`;

  try {
    const { data } = await axios.get<TTimeZoneResponse>(`${apiUrl}?${params}`);

    return data;
  } catch (err) {
    switch (err.response?.data?.status) {
      case 'ZERO_RESULTS':
      case 'INVALID_REQUEST':
        throw new CommonError('stationSetup.cantDeriveTz');

      default:
        throw err;
    }
  }
};

const getDefaultOpHrs = (): TStationOperationHours => {
  const workingHours = { startTime: '00:00:00', endTime: '23:59:59' };

  return {
    sunday: { isOpen: true, workingHours },
    monday: { isOpen: true, workingHours },
    tuesday: { isOpen: true, workingHours },
    wednesday: { isOpen: true, workingHours },
    thursday: { isOpen: true, workingHours },
    friday: { isOpen: true, workingHours },
    saturday: { isOpen: true, workingHours },
  };
};

export const prepareData = async (data: TCreateStationForm): Promise<TCreateStationPayload> => {
  const { dstOffset, rawOffset, timeZoneId } = await getTimezone(data.generalInfo.location);

  return {
    ...data,
    dstOffset,
    rawOffset,
    timezone: timeZoneId,
    operationHours: getDefaultOpHrs(),
  };
};

export type TPreparedDetailsItem = {
  label: string;
  value: number | string;
  gridProps?: GridProps;
};

export const prepareStationDetails = ({
  name,
  merchantId,
  storeId,
  lat,
  lon,
}: TCreateStationResult): TPreparedDetailsItem[] => [
  { label: 'stationSetup.complete.stationDetails.stationName', value: name, gridProps: { xs: 12 } },
  {
    label: 'stationSetup.complete.stationDetails.merchantId',
    value: merchantId,
    gridProps: { xs: 6 },
  },
  { label: 'stationSetup.complete.stationDetails.storeId', value: storeId },
  { label: 'stationSetup.complete.stationDetails.latitude', value: lat, gridProps: { xs: 6 } },
  { label: 'stationSetup.complete.stationDetails.longitude', value: lon },
];
